export const metricKeys = ['level', 'exp', 'hp', 'mp', 'mesos', 'mapName'] as const

export type MetricKey = (typeof metricKeys)[number]
export type MetricRecognitionIntervalMs = 0 | 250 | 500 | 1_000 | 2_000 | 3_000 | 4_000 | 5_000 | 10_000
export type MetricStrategy = 'bar' | 'ocr' | 'fused' | 'unavailable'
export type ReadingSource = 'bar' | 'ocr' | 'fused'
export type Availability = 'available' | 'unavailable'

export interface NormalizedRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface PixelRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface MetricReading<T = number> {
  readonly value: T | null
  readonly confidence: number
  readonly source: ReadingSource
  readonly availability: Availability
  readonly rawText?: string
  readonly ocrValue?: T
  /** The recognizer has already confirmed this value across independent OCR samples. */
  readonly confirmation?: 'consensus'
  readonly capturedAt: number
}

export interface ResourceValue {
  readonly current: number | null
  readonly max: number | null
  readonly ratio: number | null
}

export interface ExpValue {
  readonly absolute: number | null
  readonly ratio: number | null
  readonly estimated?: boolean
  /** Session-local requirement inferred from exact EXP and percentage samples. */
  readonly requiredExp?: number | null
  /** The in-game percentage is rounded, so an inferred requirement is approximate. */
  readonly requiredExpApproximate?: boolean
}

/** A verified absolute EXP reading tied to the frame that OCR actually read. */
export interface ExpExactEvidence {
  readonly value: ExpValue
  readonly level: number | null
  readonly confidence: number
  /** Decimal places shown by the in-game percentage, used to model rounding. */
  readonly percentagePrecision?: number
  readonly sourceAt: number
  readonly deliveredAt: number
  readonly confirmation: 'source-frame' | 'consensus'
}

/** A complete EXP OCR result that is not yet trusted for accounting. */
export interface ExpExactCandidate {
  readonly value: ExpValue & { readonly absolute: number; readonly ratio: number }
  readonly level: number | null
  readonly levelSourceAt: number | null
  readonly confidence: number
  readonly percentagePrecision: number
  readonly sourceAt: number
  readonly deliveredAt: number
  readonly barConsistency: 'match' | 'conflict' | 'unavailable'
  readonly recognitionPath: 'legacy' | 'adaptive'
  readonly rawText?: string
}

export type MetricDiagnosticValue = number | string | ExpValue | ResourceValue
export type MetricRecognitionStatus =
  | 'disabled'
  | 'queued'
  | 'running'
  | 'ready'
  | 'confirming'
  | 'source-hidden'
  | 'no-result'
  | 'no-text'
  | 'parse-failed'
  | 'low-confidence'

export type MetricRecognitionFailureReason =
  | 'no-text'
  | 'parse-failed'
  | 'low-confidence'

export interface MetricRecognitionDiagnostic {
  readonly status: MetricRecognitionStatus
  readonly value?: MetricDiagnosticValue
  /** Latest parseable value, which may still be awaiting confirmation. */
  readonly candidateValue?: MetricDiagnosticValue
  /** Last value published to the normal reading stream. */
  readonly acceptedValue?: MetricDiagnosticValue
  readonly confirmedSamples?: number
  readonly targetSamples?: number
  readonly pendingSince?: number
  readonly rawText?: string
  readonly confidence?: number
  readonly enhancementIndex?: number
  readonly lastAttemptAt?: number
  readonly durationMs?: number
  readonly failureReason?: MetricRecognitionFailureReason
  readonly selectedText?: string
  readonly roi?: AdaptiveMetricRoi
  readonly recognitionPath?: 'legacy' | 'adaptive'
  readonly capturedAt: number
}

export type AdaptiveRoiStatus = 'searching' | 'adapted' | 'fallback'
export type AdaptiveRoiSource = 'preset' | 'manual'
export type AdaptiveRoiMode = 'legacy' | 'candidate' | 'adaptive'
export type AdaptiveRoiFallbackReason = 'legacy-success' | 'adaptive-failed' | 'conflict'

/** Runtime-only recognition regions. Search ranges remain the only persisted ROI values. */
export interface AdaptiveMetricRoi {
  readonly searchRect: NormalizedRect
  readonly textRect: NormalizedRect
  readonly barRect?: NormalizedRect
  readonly effectiveRect: NormalizedRect
  readonly status: AdaptiveRoiStatus
  readonly mode: AdaptiveRoiMode
  readonly textMode: AdaptiveRoiMode
  readonly barMode: AdaptiveRoiMode
  readonly source: AdaptiveRoiSource
  readonly confidence: number
  readonly updatedAt: number | null
  readonly fallbackReason?: AdaptiveRoiFallbackReason
}

export interface RawReading {
  readonly capturedAt: number
  /** Identifies one uninterrupted period in which the inventory is confirmed visible. */
  readonly mesosContinuityId?: number
  readonly level: MetricReading
  readonly exp: MetricReading<ExpValue>
  readonly expExactCandidate?: ExpExactCandidate
  readonly expExactEvidence?: ExpExactEvidence
  readonly hp: MetricReading<ResourceValue>
  readonly mp: MetricReading<ResourceValue>
  readonly mesos: MetricReading
  readonly mapName: MetricReading<string>
  readonly diagnostics: {
    readonly hudConfidence: number
    readonly processingMs: number
    readonly rejectedReason?: string
    readonly metrics?: Readonly<Partial<Record<MetricKey, MetricRecognitionDiagnostic>>>
  }
}

export interface ValidatedObservation extends RawReading {
  readonly accepted: Readonly<Record<MetricKey, boolean>>
  readonly expExactEvidenceAccepted?: boolean
  /** A confirmed same-level EXP decrease that must start a new accounting baseline. */
  readonly expRebaseline?: true
  /** A confirmed same-level decrease in the independently delivered exact endpoint. */
  readonly expExactRebaseline?: true
}

export interface GameStateSnapshot {
  readonly capturedAt: number
  readonly observedAt?: Readonly<Record<MetricKey, number | null>>
  readonly level: number | null
  readonly exp: ExpValue
  readonly hp: ResourceValue
  readonly mp: ResourceValue
  readonly mesos: number | null
  readonly mapName: string | null
  readonly confidence: Readonly<Record<MetricKey, number>>
  readonly coverage: Readonly<Record<MetricKey, number>>
}

export type GameEventType =
  | 'level-up'
  | 'hp-consumed'
  | 'hp-recovered'
  | 'mp-consumed'
  | 'mp-recovered'
  | 'mesos-changed'
  | 'map-changed'
  | 'capture-interrupted'
  | 'capture-restored'

export interface GameEvent {
  readonly id: string
  readonly sessionId: string
  readonly type: GameEventType
  readonly capturedAt: number
  readonly amount?: number
  readonly from?: number
  readonly to?: number
  readonly previousMapName?: string
  readonly mapName?: string
  readonly confidence: number
}

export type SessionStatus = 'idle' | 'running' | 'paused' | 'ended' | 'interrupted'

export interface SessionAggregate {
  readonly durationMs: number
  readonly expGained: number | null
  /** True when a cross-level endpoint difference uses a runtime-inferred requirement. */
  readonly expGainedApproximate?: boolean
  /** Trusted contiguous EXP used as the hourly-rate numerator. */
  readonly expRateGained?: number | null
  /** Trusted contiguous EXP observation time used by the hourly rate. */
  readonly expTrustedDurationMs?: number
  /** Internal trusted EXP rate reserved for future leaderboard submissions. */
  readonly expTrustedPerHour?: number | null
  /** User-facing endpoint EXP gain divided by the active record duration. */
  readonly expPerHour: number | null
  /** Optional for records saved before session balance tracking was introduced. */
  readonly mesosStartBalance?: number | null
  /** Optional for records saved before session balance tracking was introduced. */
  readonly mesosCurrentBalance?: number | null
  /** Current confirmed balance minus the first confirmed balance in this record. */
  readonly mesosBalanceChange?: number | null
  readonly mesosDelta: number | null
  /** Trusted visible inventory time used by the Mesos hourly rate. */
  readonly mesosTrustedDurationMs?: number
  readonly mesosPerHour: number | null
  readonly hpConsumed: number
  readonly hpConsumedPerHour: number | null
  readonly hpRecovered: number
  readonly hpRecoveredPerHour: number | null
  readonly mpConsumed: number
  readonly mpConsumedPerHour: number | null
  readonly mpRecovered: number
  readonly mpRecoveredPerHour: number | null
  readonly coverage: Readonly<Record<MetricKey, number>>
}

export type LiveRateStatus = 'ready' | 'provisional' | 'unavailable'

export interface LiveRateMetric {
  readonly value: number | null
  readonly status: LiveRateStatus
  readonly updatedAt: number | null
}

export interface ActiveLiveRateMetric extends LiveRateMetric {
  readonly activeDurationMs: number
}

export interface TrustedLiveRateMetric extends LiveRateMetric {
  readonly trustedDurationMs: number
}

export interface LiveRateSnapshot {
  /** User-facing endpoint gain divided by the displayed active record duration. */
  readonly expPerHour: ActiveLiveRateMetric
  /** Internal trusted rate reserved for future leaderboard submissions. */
  readonly expTrustedPerHour: TrustedLiveRateMetric
  readonly mesosPerHour: TrustedLiveRateMetric
  readonly hpConsumedPerHour: TrustedLiveRateMetric
  readonly hpRecoveredPerHour: TrustedLiveRateMetric
  readonly mpConsumedPerHour: TrustedLiveRateMetric
  readonly mpRecoveredPerHour: TrustedLiveRateMetric
}

export interface SessionRecord {
  readonly id: string
  readonly presetId: string
  readonly presetVersion: number
  readonly profileId: string
  readonly status: SessionStatus
  readonly startedAt: number
  readonly endedAt?: number
  readonly activeDurationMs: number
  /** Version of the local session accounting semantics; absent on legacy records. */
  readonly sessionAccountingVersion?: number
  /** Version of the separately persisted leveling journey; absent on legacy records. */
  readonly journeyVersion?: number
  readonly pausedAt?: number
  readonly aggregate: SessionAggregate
  readonly startState?: GameStateSnapshot
  readonly endState?: GameStateSnapshot
}

export type AccountingContinuity = 'ignored' | 'baseline' | 'rebaseline' | 'contiguous' | 'discontinuous' | 'unavailable' | 'pending'

export interface AccountingMetricUpdate {
  readonly continuity: AccountingContinuity
  readonly sourceAt: number | null
  readonly trustedIntervalMs: number
}

export interface AccountingExpUpdate extends AccountingMetricUpdate {
  readonly delta: number | null
  readonly exact: boolean
  readonly start: ExpPoint | null
  readonly end: ExpPoint | null
  readonly sourceStartAt: number | null
  readonly sourceEndAt: number | null
  readonly activeStartAt: number | null
  readonly activeEndAt: number | null
}

export interface AccountingMesosUpdate extends AccountingMetricUpdate {
  readonly delta: number | null
  readonly startValue: number | null
  readonly endValue: number | null
}

export interface AccountingResourceUpdate extends AccountingMetricUpdate {
  readonly consumed: number
  readonly recovered: number
}

export interface AccountingFrame {
  readonly capturedAt: number
  readonly captureGeneration: number
  readonly exp: AccountingExpUpdate
  readonly mesos: AccountingMesosUpdate
  readonly hp: AccountingResourceUpdate
  readonly mp: AccountingResourceUpdate
}

export interface SessionIngestResult {
  readonly record: SessionRecord
  readonly accounting: AccountingFrame
}

export interface CaptureProfile {
  readonly id: string
  readonly name: string
  readonly presetId: string
  readonly presetVersion: number
  readonly sourceWidth: number
  readonly sourceHeight: number
  readonly hudRect: NormalizedRect
  readonly roiOverrides: Partial<Record<MetricKey, NormalizedRect>>
  readonly colorCalibration: Partial<Record<'hp' | 'mp' | 'exp', readonly [number, number, number]>>
  readonly recognition: {
    readonly captureFps: number
    /** Legacy frequency multipliers. Read only while migrating older profiles. */
    readonly metricCaptureFps?: Readonly<Partial<Record<MetricKey, number>>>
    /** Legacy frequency settings version. Read only while migrating older profiles. */
    readonly metricCaptureFpsVersion?: number
    readonly metricRecognitionIntervalsMs?: Readonly<Partial<Record<MetricKey, MetricRecognitionIntervalMs>>>
    readonly metricRecognitionIntervalsVersion?: number
    readonly minConfidence: number
    readonly ocrChangeThreshold: number
  }
  readonly createdAt: number
  readonly updatedAt: number
}

export interface AppSetting<T = unknown> {
  readonly key: string
  readonly value: T
  readonly updatedAt: number
}

export interface ExpPoint {
  readonly level: number
  readonly absolute: number
  readonly ratio: number
  /** Null only for a zero-percent endpoint before this level has enough samples. */
  readonly requiredExp: number | null
  readonly requiredExpApproximate: boolean
}

export const SESSION_JOURNEY_VERSION = 1

export type SessionJourneyContinuity = 'baseline' | 'contiguous' | 'discontinuous' | 'rebaseline'

export interface SessionJourneyPoint {
  readonly activeDurationMs: number
  readonly capturedAt: number
  readonly expGained: number
  readonly level: number
  readonly expAbsolute: number
  readonly expRatio: number
  readonly mapName: string | null
  readonly approximate: boolean
  readonly continuity: SessionJourneyContinuity
  readonly anchor: boolean
}

export type SessionJourneyEventType =
  | 'level-up'
  | 'map-change'
  | 'pause'
  | 'resume'
  | 'capture-interrupted'
  | 'capture-restored'
  | 'rebaseline'

export interface SessionJourneyEvent {
  readonly type: SessionJourneyEventType
  readonly activeDurationMs: number
  readonly capturedAt: number
  readonly fromLevel?: number
  readonly toLevel?: number
  readonly previousMapName?: string
  readonly mapName?: string
}

export interface SessionJourney {
  readonly sessionId: string
  readonly version: number
  readonly startedAt: number
  readonly updatedAt: number
  readonly bucketSizeMs: number
  readonly points: readonly SessionJourneyPoint[]
  readonly events: readonly SessionJourneyEvent[]
}
